/* eslint-disable */

/**
 * The reason an authorization was declined, which may come from either Square's payment
 * stack or the issuing bank.
 */
export enum AuthorizationError {
  /**
   * DEFAULT_ENUM_DO_NOT_USE - First value is default in many language bindings and is explicit to be this
   * 'unset' value so debuggers are less confusing than displaying value 1 below.
   */
  DEFAULT_ENUM_DO_NOT_USE = "DEFAULT_ENUM_DO_NOT_USE",
  /**
   * INVALID_EXPIRATION - Square's payment stack determined the expiration date is improperly formatted or missing and therefore
   * never sent it to our external partner
   *
   * Not to be confused with CARD_EXPIRED or EXPIRATION_FAILURE
   */
  INVALID_EXPIRATION = "INVALID_EXPIRATION",
  /**
   * INVALID_ACCOUNT - Used when the issuing bank fails to locate the account on their record.
   *
   * Note the difference with PAN_FAILURE
   */
  INVALID_ACCOUNT = "INVALID_ACCOUNT",
  /**
   * BLACKLISTED_ACCOUNT - Deprecated. Use CARDHOLDER_INSUFFICIENT_PERMISSIONS instead.
   *
   * @deprecated
   */
  BLACKLISTED_ACCOUNT = "BLACKLISTED_ACCOUNT",
  /**
   * CURRENCY_MISMATCH - The funding source is limited in which currencies it can use, and this
   * isn't one of the good ones. For example, gift cards are limited to a single
   * currency.
   */
  CURRENCY_MISMATCH = "CURRENCY_MISMATCH",
  /**
   * INSUFFICIENT_FUNDS - The funding source has insufficient funds to cover the payment. When
   * partial auths are permitted, this error indicates a complete lack of funds.
   * Otherwise it indicates the available amount is less than the requested
   * amount.
   *
   * Used for debit card, prepaid card or gift cards only. Note the difference with TRANSACTION_LIMIT.
   */
  INSUFFICIENT_FUNDS = "INSUFFICIENT_FUNDS",
  /**
   * MERCHANT_MISMATCH - Deprecated. Use CARDHOLDER_INSUFFICIENT_PERMISSIONS instead
   *
   * @deprecated
   */
  MERCHANT_MISMATCH = "MERCHANT_MISMATCH",
  /**
   * MERCHANT_INSUFFICIENT_PERMISSIONS - The receiving merchant doesn't have the necessary permissions to accept
   * this payment. For example, Square may limit which merchants are allowed to
   * receive gift card payments. Square may also revoke a merchant's ability to
   * receive balance payments due to risk or other signals.
   */
  MERCHANT_INSUFFICIENT_PERMISSIONS = "MERCHANT_INSUFFICIENT_PERMISSIONS",
  /**
   * LOCATION - The merchant's location is not in his allowed region. For example, a US merchant
   * is not allowed to accept payments in Japan or Canada.
   *
   * Note the difference with CARD_NOT_SUPPORTED.
   */
  LOCATION = "LOCATION",
  /**
   * TRANSACTION_LIMIT - The payment amount is not within the allowed transaction limit; it is either
   * too low or too high. Mostly used for credit cards. However some times the issuer bank indicates
   * the error is either insufficient funds (for debit/prepaid cards) or credit limit hit (for credit
   * cards). In that case, we will use this to present a more ambiguous error message to
   * the client.
   *
   * Note the difference with INSUFFICIENT_FUNDS.
   */
  TRANSACTION_LIMIT = "TRANSACTION_LIMIT",
  /**
   * OTHER - The payment was declined for any other reason where that reason is technical in nature.
   * Originally the definition of OTHER was not limited to "technical in nature". Now this value is
   * meant as a catch-all for failures within the control of Square or its partners. Examples of
   * such failures are unsupported action, formatting error, and invalid amount. The catch-all for
   * failures for non-technical reasons is OTHER_NON_TECHNICAL.
   */
  OTHER = "OTHER",
  /**
   * INVALID_EXPIRATION_YEAR - Deprecated. Use INVALID_EXPIRATION instead.
   *
   * @deprecated
   */
  INVALID_EXPIRATION_YEAR = "INVALID_EXPIRATION_YEAR",
  /**
   * INVALID_EXPIRATION_MONTH - Deprecated. Use INVALID_EXPIRATION instead.
   *
   * @deprecated
   */
  INVALID_EXPIRATION_MONTH = "INVALID_EXPIRATION_MONTH",
  /**
   * CARD_EXPIRED - Square's payment stack determines the credit card has expired and never sent it to
   * our external partner.
   *
   * Not to be confused with INVALID_EXPIRATION or EXPIRATION_FAILURE
   */
  CARD_EXPIRED = "CARD_EXPIRED",
  /** CVV_FAILURE - Used when the issuer declines the transaciton due to CVV failure. Note this could be flaky. */
  CVV_FAILURE = "CVV_FAILURE",
  /**
   * AVS_FAILURE - Used when the postal code verification fails for keyed in transactions. Note this could
   * be flaky.
   */
  AVS_FAILURE = "AVS_FAILURE",
  /**
   * GATEWAY_TIME_OUT - Used when our authorization call to the external partner timed out. If we don't know if it
   * succeeded or not, we will assume that the authorize request was declined.
   */
  GATEWAY_TIME_OUT = "GATEWAY_TIME_OUT",
  /** VOICE_FAILURE - Declined because the issuer requires voice authorization with the cardholder. */
  VOICE_FAILURE = "VOICE_FAILURE",
  /**
   * PAN_FAILURE - Used under one of the following scenarios:
   *
   * The card number contains the incorrect length or is improperly formatted.
   * The card number fails the Luhn check (incorrect check digit).
   * The card brand is unknown
   *
   * Note the difference with INVALID_ACCOUNT
   */
  PAN_FAILURE = "PAN_FAILURE",
  /**
   * EXPIRATION_FAILURE - Square's internal payment stack did not detect any problem with the expiration date. However, our
   * external partner returns a error code that indicates the expiration date does not match the date
   * on their record, or the card has expired, or the date is is improperly formatted. This could be flaky.
   */
  EXPIRATION_FAILURE = "EXPIRATION_FAILURE",
  /** MERCHANT_BLOCKED_BY_RISK - Occurs when Risk proactively rejects an authorization in the case of store-and-forward payments. */
  MERCHANT_BLOCKED_BY_RISK = "MERCHANT_BLOCKED_BY_RISK",
  /**
   * CARD_NOT_SUPPORTED - The card is not supported at this region or for a certain merchant type. For example,
   * as of April 2015 we accept JCB cards in US but not in Japan.
   */
  CARD_NOT_SUPPORTED = "CARD_NOT_SUPPORTED",
  /**
   * CARDHOLDER_INSUFFICIENT_PERMISSIONS - The funding source is limited in which merchants it can pay. For example, gift cards
   * may be limited to a single merchant.
   *
   * Or, when our external partner declined the transaction because the cardholder does not have
   * sufficient permissions to perform this type of transaction or for a certain business type.
   */
  CARDHOLDER_INSUFFICIENT_PERMISSIONS = "CARDHOLDER_INSUFFICIENT_PERMISSIONS",
  /**
   * INVALID_REQUEST - The client (at Square) failed to set certain fields in the correct format or certain tokens
   * in the request are not recognized.
   */
  INVALID_REQUEST = "INVALID_REQUEST",
  /** INVALID_PIN - Invalid PIN entered by the cardholder. Only applicable for online PIN transactions. */
  INVALID_PIN = "INVALID_PIN",
  /** INVALID_POSTAL_CODE - The postal code is improperly formatted. */
  INVALID_POSTAL_CODE = "INVALID_POSTAL_CODE",
  /**
   * PAYMENT_BLOCKED_BY_RISK - Occurs when Risk proactively rejects an authorization before the auth result
   * is returned to the client, used for connect online payments.
   */
  PAYMENT_BLOCKED_BY_RISK = "PAYMENT_BLOCKED_BY_RISK",
  /**
   * CHIP_INSERTION_REQUIRED - In some situations, like after too many contactless transactions in a row, the issuer might request
   * a dip.
   */
  CHIP_INSERTION_REQUIRED = "CHIP_INSERTION_REQUIRED",
  /**
   * ALLOWABLE_PIN_TRIES_EXCEEDED - The issuer can respond with a particular response code that indicates that the card has used up its
   * allowable PIN tries. This response code typically implies that the cardholder should contact his/her
   * issuer to "unblock" the card.
   */
  ALLOWABLE_PIN_TRIES_EXCEEDED = "ALLOWABLE_PIN_TRIES_EXCEEDED",
  /**
   * PAYMENT_DECLINED_BY_SHADOWBAN - The merchant is flagged for shadowban and therefore all auth requests should be declined.
   * Design docs:
   * https://docs.google.com/document/d/1CU-Bim65Bjw2EMwH-o_rc5fA1KIYLBESYeBUzU7-dn0/edit#heading=h.hin1vkdjfekl
   * https://docs.google.com/document/d/1tBS_hBpGfosak0bb4phrGMPfmCfQUyDogD8WUEI1F3E/edit#heading=h.9y7prw62x3zs
   */
  PAYMENT_DECLINED_BY_SHADOWBAN = "PAYMENT_DECLINED_BY_SHADOWBAN",
  /**
   * MANUALLY_ENTERED_PAYMENT_NOT_SUPPORTED - This payment was declined because keying-in / manually entering this payment
   * was disallowed. This can occur in the case of JCB in JP, where JCB wants
   * to restrict Square from accepting keyed-in payments for JCB cards.
   */
  MANUALLY_ENTERED_PAYMENT_NOT_SUPPORTED = "MANUALLY_ENTERED_PAYMENT_NOT_SUPPORTED",
  /**
   * NO_ROUTE_FOUND - No route was found for routing this particular payment. No external
   * requests were sent for this payment. This can occur transiently -- all
   * possible gateways that could handle this request are down -- or occur
   * permanently -- there are no possible gateways that can handle this request.
   */
  NO_ROUTE_FOUND = "NO_ROUTE_FOUND",
  /**
   * INVALID_FEES - Fees attached to this payment are not valid. e.g. No matching fee selectors,
   * payment attributes do not match, exceeded payment amount, etc
   */
  INVALID_FEES = "INVALID_FEES",
  /**
   * PAYMENT_LIMIT_EXCEEDED - This authorization was declined by Square due to a program called Payment Limits, which is used
   * primarily by risk teams at Square to impose processing limits on certain merchants due to
   * risk reasons. This error is used when that processing limit would be exceeded by this payment.
   * see: go/PaymentLimitsDesignDoc
   */
  PAYMENT_LIMIT_EXCEEDED = "PAYMENT_LIMIT_EXCEEDED",
  /**
   * AUTHENTICATION_REQUIRED - This authorization either did not provide authentication details (ECI and CAVV values), or
   * provided invalid details, and was as such rejected by the issuer.
   */
  AUTHENTICATION_REQUIRED = "AUTHENTICATION_REQUIRED",
  /**
   * ASSET_BLOCKED - This authorization was declined by the customer through Blocklist. The asset (card, IP, etc) has been blocked
   * by the merchant and/or the marketplace account.
   * see: go/whatis/blocklist
   *
   * TODO(mkim, 2/14/2020, FRDENG-1223): Deprecate.
   */
  ASSET_BLOCKED = "ASSET_BLOCKED",
  /**
   * PAYMENT_BLOCKED_BY_MERCHANT_CONFIGURED_RULE - This authorization was declined by one or more customer-defined rules. Only applies to
   * API payments.
   * eng doc: go/RuliusEngDoc
   * see: go/whatis/rulius
   */
  PAYMENT_BLOCKED_BY_MERCHANT_CONFIGURED_RULE = "PAYMENT_BLOCKED_BY_MERCHANT_CONFIGURED_RULE",
  /**
   * PAYMENT_BLOCKED_PRE_AUTH_BY_DECLINE_RATE_LIMIT - This authorization was declined pre-auth, and recorded as failed. The result was based on
   * rate-limiting applied to recently declined payments with similar parameters to the current
   * payment. Intended for simple api-level carding fraud mitigation.
   */
  PAYMENT_BLOCKED_PRE_AUTH_BY_DECLINE_RATE_LIMIT = "PAYMENT_BLOCKED_PRE_AUTH_BY_DECLINE_RATE_LIMIT",
  /**
   * PAN_BLOCKED_BY_MERCHANT - This authorization was declined pre-auth by the customer through Blocklist. The card
   * has been blocked by the merchant and/or the marketplace account.
   * see: go/whatis/blocklist
   */
  PAN_BLOCKED_BY_MERCHANT = "PAN_BLOCKED_BY_MERCHANT",
  /**
   * CLIENT_IP_BLOCKED_BY_RISK - This authorization was declined pre-auth by Square through Blocklist. The client IP address
   * has been blocked by a Square agent or an automated internal rule.
   * see: go/whatis/blocklist
   */
  CLIENT_IP_BLOCKED_BY_RISK = "CLIENT_IP_BLOCKED_BY_RISK",
  /**
   * BROWSER_FINGERPRINT_BLOCKED_BY_RISK - This authorization was declined pre-auth by Square through Blocklist. The browser fingerprint has been
   * blocked on this merchant by a Square agent or an automated internal rule.
   * see: go/whatis/blocklist
   */
  BROWSER_FINGERPRINT_BLOCKED_BY_RISK = "BROWSER_FINGERPRINT_BLOCKED_BY_RISK",
  /**
   * OTHER_NON_TECHNICAL - The payment was declined for any other reason where that reason is non-technical in
   * nature. This value is meant as a catch-all for failures that are beyond the control of Square
   * or its partners. Examples of such failures are lost or stolen card, account frozen, and
   * suspected fraud. The catch-all for failures for technical reasons is OTHER.
   */
  OTHER_NON_TECHNICAL = "OTHER_NON_TECHNICAL",
  /** SUSPECTED_FRAUD - The payment was declined because the issuer suspects the transaction could be fraudulent. */
  SUSPECTED_FRAUD = "SUSPECTED_FRAUD",
  /** ACCOUNT_CLOSED - The payment was declined because the issuer says the account has been closed. */
  ACCOUNT_CLOSED = "ACCOUNT_CLOSED",
  /**
   * ACCOUNT_BLOCKED_BY_ISSUER - The payment was declined because the issuer says there is a block on the account. This could
   * be for a new account that hasn't been activated, or for a temporary block on an account.
   */
  ACCOUNT_BLOCKED_BY_ISSUER = "ACCOUNT_BLOCKED_BY_ISSUER",
  /**
   * CARD_BLOCKED_BY_RISK - This authorization was declined pre-auth by Square through Blocklist. The card (PAN + Exp Date)
   * has been blocked by a Square agent or an automated internal rule.
   * see: go/whatis/blocklist
   */
  CARD_BLOCKED_BY_RISK = "CARD_BLOCKED_BY_RISK",
  /**
   * EMAIL_BLOCKED_BY_RISK - This authorization was declined pre-auth by Square through Blocklist. The email address
   * has been blocked by a Square agent or an automated internal rule.
   * see: go/whatis/blocklist
   */
  EMAIL_BLOCKED_BY_RISK = "EMAIL_BLOCKED_BY_RISK",
  /**
   * PAN_BLOCKED_BY_RISK - This authorization was declined pre-auth by Square through Blocklist. The PAN
   * has been blocked by a Square agent or an automated internal rule.
   * see: go/whatis/blocklist
   */
  PAN_BLOCKED_BY_RISK = "PAN_BLOCKED_BY_RISK",
  /**
   * PAYMENT_AMOUNT_MISMATCH - The payment was declined due to payment amount mismatch. This applies to payments methods
   * where the creation of the source id (e.g. a nonce payment) actually includes the payment
   * amount within the source tokenization. The error occurs when this amount and what is
   * sent to Square are mismatched.
   */
  PAYMENT_AMOUNT_MISMATCH = "PAYMENT_AMOUNT_MISMATCH",
  /**
   * PAYMENT_BLOCKED_BY_TRAFFIC_REMEDIATION - The payment request was rejected by Esperanto's internal traffic evaluation before it was sent
   * to a gateway. This happens when Esperanto is having issues with a certain type of traffic and
   * needs to reject that traffic to remediate those issues.
   */
  PAYMENT_BLOCKED_BY_TRAFFIC_REMEDIATION = "PAYMENT_BLOCKED_BY_TRAFFIC_REMEDIATION",
}
